import * as Sentry from '@sentry/react';
import { API_BASE_URL, SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type {
  GetUserView,
  JwtView,
  OrganizationView,
  UserLoginSchema,
  UserSetPasswordSchema,
  UserView,
} from '../types';
import { APIError } from './api-error';
import { fetchWithToken } from './fetch-with-token';

async function readErrorBody(response: Response) {
  try {
    return await response.clone().json();
  } catch {
    return await response.text();
  }
}

async function handleError(response: Response, request: Request) {
  const body = await readErrorBody(response);

  throw new APIError(typeof body === 'string' ? body : JSON.stringify(body), response, request);
}

/**
 * Gets a user by their ID.
 *
 * @param userId - The ID of the user to get.
 * @returns {Promise<GetUserResponse>} User data, like email address, default organization ID, and default portfolio ID.
 */
async function getUser(userId: string): Promise<GetUserView> {
  const url = `${API_BASE_URL}/v1/users/${userId}`;
  const method = 'GET';

  const response = await fetchWithToken(url, { method });

  if (!response.ok) {
    await handleError(response, new Request(url, { method }));
  }

  return (await response.json()) as GetUserView;
}

/**
 * Gets an organization by its ID.
 *
 * @param organizationId - The ID of the organization to get.
 * @returns {Promise<OrganizationView>} Organization data.
 */
async function getOrganization(organizationId: string): Promise<OrganizationView> {
  Sentry.addBreadcrumb({
    message: 'Requesting organization data',
    level: 'info',
    data: { organization_id: organizationId },
  });

  const url = `${API_BASE_URL}/v1/organizations/${organizationId}`;
  const method = 'GET';

  const response = await fetchWithToken(url, { method });

  if (!response.ok) {
    await handleError(response, new Request(url, { method }));
  }

  return (await response.json()) as OrganizationView;
}

/**
 * The result of a successful login.
 */
interface LoginResult {
  /** The organization ID the user belongs to. */
  organization_id: string;
  /** The authenticated user's ID. */
  user_id: string;
  /** The default portfolio ID for the user's organization. */
  default_portfolio_id: string;
  /** The name of the user's organization. */
  organization_name: string;
}

/**
 * Authenticates a user and returns their login data.
 * Throws an {@link APIError} if authentication fails or user data is invalid.
 *
 * @param requestBody - The login credentials.
 * @returns The authenticated user's organization and identity data.
 */
async function loginUser(requestBody: UserLoginSchema): Promise<LoginResult> {
  const url = `${API_BASE_URL}/v1/users/authenticate`;
  const method = 'POST';
  const headers = {
    'Content-Type': 'application/json',
  };
  const body = JSON.stringify(requestBody);

  const response = await fetchWithToken(url, { method, headers, body });

  if (!response.ok) {
    // Track the failed authentication as a breadcrumb before throwing.
    Sentry.withScope((scope) => {
      scope.setFingerprint([method, url, String(response.status)]);

      Sentry.addBreadcrumb({
        message: 'Failed to authenticate user.',
        level: 'error',
        data: { response_status: response.status },
      });
    });

    await handleError(response, new Request(url, { method, headers, body }));
  }

  const jwtTokenData = (await response.json()) as JwtView;

  if (jwtTokenData?.user_id) {
    Sentry.withScope((scope) => {
      scope.setUser({ id: jwtTokenData.user_id });

      Sentry.addBreadcrumb({
        message: 'User authenticated successfully.',
        level: 'info',
        data: { user_id: jwtTokenData.user_id },
      });
    });

    // Get organization and portfolio data for the user.
    const userData = await getUser(jwtTokenData.user_id);

    const { user_id, organization_id } = jwtTokenData ?? {};
    const { default_organization_name, default_portfolio_id } = userData ?? {};

    if (default_portfolio_id && organization_id && default_organization_name && user_id) {
      Sentry.addBreadcrumb({
        message: 'Login successful.',
        level: 'info',
        data: { user_id, organization_id },
      });

      return {
        default_portfolio_id,
        organization_id,
        organization_name: default_organization_name,
        user_id,
      };
    }

    const error = new Error(`Invalid user data for user (${user_id}).`);

    Sentry.withScope((scope) => {
      scope.setUser({ id: user_id });

      Sentry.captureException(error, {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Authentication,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].loginUser,
        },
        extra: {
          user_id,
          missing_fields: {
            default_portfolio_id: !default_portfolio_id,
            organization_id: !organization_id,
            default_organization_name: !default_organization_name,
            user_id: !user_id,
          },
        },
      });
    });

    throw error;
  }

  const error = new Error('No user ID found in JWT token data.');

  // Capture this error as an exception, this would be pretty bad if this happens.
  Sentry.withScope((scope) => {
    scope.setFingerprint([method, url, String(response.status)]);

    Sentry.captureException(error, {
      level: 'error',
      tags: {
        feature: SENTRY_FEATURE_TAGS.Authentication,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].loginUser,
      },
      extra: { response_status: response.status },
    });
  });

  throw error;
}

/**
 * Logs out a user by setting JWT token to 'deleted' in cookies and expires the JWT token, server-side.
 *
 * @param userId - The ID of the user to logout.
 * @param onError - The callback to call if the logout fails.
 */
async function logoutUser(userId: string, onError?: (error: unknown) => void) {
  try {
    const url = `${API_BASE_URL}/v1/users/${userId}/logout`;
    const method = 'POST';

    const response = await fetchWithToken(url, { method });

    if (response.ok) {
      Sentry.addBreadcrumb({
        message: 'Logout successful.',
        level: 'info',
        data: { user_id: userId },
      });
    }
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        feature: SENTRY_FEATURE_TAGS.Authentication,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].logout,
      },
      extra: {
        user_id: userId,
      },
    });

    onError?.(error);
  }
}

async function setPassword(
  params: UserSetPasswordSchema & { user_id: string },
  onSuccess?: (data: UserView) => Promise<void>,
  onError?: (error: unknown | APIError) => void,
) {
  try {
    const { user_id, ...requestBody } = params;

    const url = `${API_BASE_URL}/v1/users/${user_id}/password`;
    const method = 'POST';
    const headers = {
      'Content-Type': 'application/json',
    };
    const body = JSON.stringify(requestBody);

    const response = await fetchWithToken(url, { method, headers, body });

    if (!response.ok) {
      await handleError(response, new Request(url, { method, headers, body }));
    }

    const data = (await response.json()) as UserView;

    Sentry.addBreadcrumb({
      message: 'Password set successfully.',
      level: 'info',
      data: { user_id },
    });

    onSuccess?.(data);
  } catch (error) {
    Sentry.captureException(error, {
      level: 'warning',
      tags: {
        feature: SENTRY_FEATURE_TAGS.Authentication,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Authentication].setPassword,
      },
      extra: { user_id: params.user_id },
    });

    onError?.(error);
  }
}
type SsoCheckResponse = {
  sso: boolean;
  connections: Array<{ label: string; slug: string }>;
};

type SsoAuthorizeResponse = {
  authorize_url: string;
};

async function ssoCheck(email: string): Promise<SsoCheckResponse> {
  const url = `${API_BASE_URL}/v1/auth/sso-check?email=${encodeURIComponent(email)}`;
  const method = 'GET';

  const response = await fetchWithToken(url, { method });

  if (!response.ok) {
    await handleError(response, new Request(url, { method }));
  }

  return (await response.json()) as SsoCheckResponse;
}

async function ssoAuthorize(params: {
  orgSlug: string;
  loginHint: string;
  redirect: string;
}): Promise<SsoAuthorizeResponse> {
  const { orgSlug, loginHint, redirect } = params;
  const url = `${API_BASE_URL}/v1/auth/sso-authorize?org_slug=${encodeURIComponent(orgSlug)}&login_hint=${encodeURIComponent(loginHint)}&redirect=${encodeURIComponent(redirect)}`;
  const method = 'GET';

  const response = await fetchWithToken(url, { method });

  if (!response.ok) {
    await handleError(response, new Request(url, { method }));
  }

  return (await response.json()) as SsoAuthorizeResponse;
}

export { getUser, getOrganization, loginUser, logoutUser, setPassword, ssoCheck, ssoAuthorize };
